import React, { useState } from 'react';
import { logError } from 'utils/logger';
import { Nullable } from 'global';
import { LinearProgress, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useNotifications } from 'modules/notification';
import { FormProvider, useForm } from 'react-hook-form';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { useVO } from '../../hooks/useVO';
import { CreditLineFormValues } from '../model';
import { CreditLineForm } from './CreditLineForm';
import {
  CreateCreditLineRequestInput,
  CreditLineRequest,
  CreditLineStatusEnum,
  LenderProfile,
  UpdateCreditLineRequestInput,
} from '@trnsact/trnsact-shared-types';

interface Props {
  VOId: string;
  userName: string;
  isCreditLineRequested: boolean;
  lenderProfiles: LenderProfile[];
  expirationDate: Nullable<Date>;
  creditLineRequest: Nullable<CreditLineRequest>;
  creditLineRequestLegacyData?: {
    notes: Nullable<string>;
    status: Nullable<CreditLineStatusEnum>;
    VOAmount: Nullable<number>;
    creditAppAmount: Nullable<number>;
  };
}

export const CreditLineCard = ({
  VOId,
  userName,
  isCreditLineRequested,
  lenderProfiles,
  creditLineRequest,
  creditLineRequestLegacyData,
}: Props) => {
  const classes = useStyles();

  const { showNotification } = useNotifications();

  const form = useForm<CreditLineFormValues>({
    defaultValues: {
      status: creditLineRequest?.status || CreditLineStatusEnum.New,
      notes: creditLineRequest?.notes || '',
      requestedAmount: creditLineRequest?.requestedAmount || 0,
      approvedAmount: creditLineRequest?.approvedAmount || 0,
      allNotes: creditLineRequest?.notes || '',
      expirationDate: creditLineRequest?.expirationDate ? new Date(creditLineRequest.expirationDate) : null,
      lenderProfileId: creditLineRequest?.lenderProfileId || null,
      approvedBy: null,
      approvedOn: null,
    },
  });

  const [isCreditLineUpdating, setIsCreditLineUpdating] = useState(false);

  const { createCreditLineRequest, updateCreditLineRequest } = useVO();

  const handleUpdateCreditLineRequest = async (data: UpdateCreditLineRequestInput) => {
    try {
      setIsCreditLineUpdating(true);
      await updateCreditLineRequest(data);
      setIsCreditLineUpdating(false);
    } catch (error) {
      logError(error);
      setIsCreditLineUpdating(false);
    }
  };

  const handleCreateCreditLineRequest = async (data: CreateCreditLineRequestInput) => {
    try {
      setIsCreditLineUpdating(true);
      await createCreditLineRequest(data);
      showNotification('Credit line successfully updated!');
      setIsCreditLineUpdating(false);
    } catch (error) {
      logError(error);
      showNotification('Update to credit line failed!', { type: 'error' });
      setIsCreditLineUpdating(false);
    }
  };

  return (
    <CardContainer title="Credit Line">
      <>
        {!isCreditLineRequested ? (
          <Typography>Not Requested</Typography>
        ) : (
          <form className={classes.cardContainer}>
            <FormProvider {...form}>
              <CreditLineForm
                status={form.getValues('status')}
                userName={userName}
                requestedAmount={form.getValues('requestedAmount')}
                approvedAmount={form.getValues('approvedAmount')}
                onCreditLineUpdate={handleUpdateCreditLineRequest}
                onCreditLineCreate={handleCreateCreditLineRequest}
                lenderProfiles={lenderProfiles}
                creditLineRequest={creditLineRequest ? creditLineRequest : undefined}
              />
            </FormProvider>
          </form>
        )}
      </>
      {isCreditLineUpdating && (
        <div>
          Updating Credit Line...
          <LinearProgress style={{ width: '100%' }} />
        </div>
      )}
    </CardContainer>
  );
};

const useStyles = makeStyles({
  cardContainer: {
    gap: '1rem',
    display: 'flex',
    flexDirection: 'column',

    '& .lineSection': {
      gap: '1rem',
      display: 'flex',
      flexDirection: 'column',

      '& .form': {
        gap: '1rem',
        display: 'flex',
        flexDirection: 'column',
      },
    },

    '& .notesSection': {
      gap: '1rem',
      display: 'flex',
      flexDirection: 'column',

      '& .customNote': {
        gap: '1rem',
        display: 'flex',
        alignItems: 'flex-end',

        '& > .addNotesBtn': {
          minWidth: 120,
        },
      },
    },
  },
});
